import { FILTER_FEATURE } from "api/ApiPath";
import { BaseApiResponseModel } from "app/based/baseApiModel/baseApiResponseModel/BaseApiResponseModel";
import { ListBaseApiResponseModel } from "app/based/baseApiModel/baseApiResponseModel/ListBaseApiResponseModel";
import { BaseFilterListRequestModel } from "app/based/baseApiModel/baseFilterListModel/BaseFilterListRequestModel";
import { BaseFilterListResponseModel } from "app/based/baseApiModel/baseFilterListModel/BaseFilterListResponseModel";
import { BaseUserRequestModel } from "app/based/baseApiModel/baseUserModel/BaseUserRequestModel";
import { BaseUserResponseModel } from "app/based/baseApiModel/baseUserModel/BaseUserResponseModel";
import { FilterInjection } from "app/filters/di/FilterInjection";
import { FilterUseCase } from "app/filters/domain/useCase/FilterUseCase";
import { CashFlowRequestModel } from "features/cashFlow/data/model/CashFlowRequestModel";
import { CashFlowResponseModel } from "features/cashFlow/data/model/CashFlowResponseModel";
import { CashFlowInjection } from "features/cashFlow/di/CashFlowInjection";
import { injectable } from "tsyringe";
import { DateFormat } from "utils/format/DateFormat";
import { paramsChecking } from "utils/helpersInTs/helpersInTs";
import { ExportPayload } from "../entities/CashFlowPayload";
import {
  CashFlowQuery,
  SearchContext,
  SearchFormValues,
} from "../entities/CashFlowQuery";
import { ICashFlowRepository } from "../repositories/ICashFlowRepository";

@injectable()
export class CashFlowUseCase {
  private cashFlowRepo: ICashFlowRepository;
  private filterUseCase: FilterUseCase;

  constructor() {
    this.cashFlowRepo = CashFlowInjection.getRepo();
    this.filterUseCase = FilterInjection.getUseCase();
  }

  private prepareFormValues(
    rawFormValues: Record<string, unknown>,
  ): SearchFormValues {
    return {
      type: paramsChecking(rawFormValues?.type, "select") as number | string,
      code: paramsChecking(rawFormValues?.code, "input") as string,
      transRef: paramsChecking(rawFormValues?.transRef, "input") as string,
      supplierRefCode: paramsChecking(
        rawFormValues?.supplierRefCode,
        "input",
      ) as string,
      phoneNumber: paramsChecking(
        rawFormValues?.phoneNumber,
        "input",
      ) as string,
      partnerCode: paramsChecking(
        rawFormValues?.partnerCode,
        "select",
      ) as string,
      userRef: (rawFormValues?.userRef as string[]) || undefined,
    };
  }

  private getDateRange(rawFormValues: Record<string, unknown>): {
    dateFrom: string;
    dateTo: string;
  } {
    const dateFrom =
      DateFormat.dateRendering(
        rawFormValues?.created as Array<Record<string, unknown>>,
        "from",
      ) ?? "";
    const dateTo =
      DateFormat.dateRendering(
        rawFormValues?.created as Array<Record<string, unknown>>,
        "to",
      ) ?? "";
    return { dateFrom, dateTo };
  }

  async getList(
    params: CashFlowRequestModel,
  ): Promise<ListBaseApiResponseModel<CashFlowResponseModel>> {
    return await this.cashFlowRepo.getList(params);
  }

  async getListFromSearch(
    rawFormValues: Record<string, unknown>,
    context: SearchContext,
  ): Promise<{
    list: ListBaseApiResponseModel<CashFlowResponseModel>;
    params: CashFlowRequestModel;
  }> {
    const formValues = this.prepareFormValues(rawFormValues);
    const { dateFrom, dateTo } = this.getDateRange(rawFormValues);
    const params = CashFlowQuery.buildSearchQuery(
      formValues,
      context,
      dateFrom,
      dateTo,
    );
    const list = await this.cashFlowRepo.getList(params);
    return { list, params };
  }

  async getListWithTableChange(
    prevParams: CashFlowRequestModel,
    rawFormValues: Record<string, unknown>,
    page: number,
    limit: number,
  ): Promise<{
    list: ListBaseApiResponseModel<CashFlowResponseModel>;
    params: CashFlowRequestModel;
  }> {
    const formValues = this.prepareFormValues(rawFormValues);
    const params = CashFlowQuery.buildTableChangeQuery(
      prevParams,
      formValues,
      page,
      limit,
    );
    const list = await this.cashFlowRepo.getList(params);
    return { list, params };
  }

  async getListRedo(): Promise<{
    list: ListBaseApiResponseModel<CashFlowResponseModel>;
    params: CashFlowRequestModel;
  }> {
    const { fromDate, toDate } = DateFormat.getDateRange(0);
    const params = CashFlowQuery.buildRedoQuery(fromDate ?? "", toDate ?? "");
    const list = await this.cashFlowRepo.getList(params);
    return { list, params };
  }

  async getDetail(
    params: CashFlowRequestModel,
  ): Promise<BaseApiResponseModel<CashFlowResponseModel>> {
    return await this.cashFlowRepo.getDetail(params);
  }

  async exportReport(
    params: CashFlowRequestModel,
  ): Promise<BaseApiResponseModel<Blob>> {
    return await this.cashFlowRepo.exportReport(params);
  }

  async exportReportFromSearch(
    payload: ExportPayload,
  ): Promise<BaseApiResponseModel<Blob>> {
    const formValues = this.prepareFormValues(payload.rawFormValues);
    const { dateFrom, dateTo } = this.getDateRange(payload.rawFormValues);
    const params = CashFlowQuery.buildExportQuery(
      formValues,
      payload.context,
      dateFrom,
      dateTo,
    );
    return await this.cashFlowRepo.exportReport(params);
  }

  async getFilterList(
    params: BaseFilterListRequestModel,
    target: "carrier" | "partner" | "store" | "denomination",
  ): Promise<Array<BaseFilterListResponseModel>> {
    let path = "";
    switch (target) {
      case "carrier":
        path = FILTER_FEATURE.CARRIER_LIST;
        break;
      case "partner":
        path = FILTER_FEATURE.PARTNER_LIST;
        break;
      case "store":
        path = FILTER_FEATURE.STORE_LIST;
        break;
      case "denomination":
        path = FILTER_FEATURE.DENOMINATION_LIST;
        break;
    }
    return await this.filterUseCase.fetchListWithFeature({
      path,
      params,
    });
  }

  async getUserForFilter(
    params: BaseUserRequestModel,
    target: "staff" | "userB2B",
  ): Promise<Array<BaseUserResponseModel>> {
    let path = "";
    switch (target) {
      case "staff":
        path = FILTER_FEATURE.STAFF_LIST;
        break;
      case "userB2B":
        path = FILTER_FEATURE.USER_LIST_B2B;
        break;
    }
    return await this.filterUseCase.fetchListWithFeature({
      path,
      params,
    });
  }
}
